/*
 * Build timeline tab: pure model, no React. Pairs the phase events into phases, writes the title and detail of each
 * event (table-driven by kind) and picks the tones of the phase chips and the event dots.
 */
import type { BuildEvent } from '@/api/schemas';
import { humanize } from '@/lib/format';
import { anyStatusLabel, phaseEventTitle } from '../buildLabels';

export interface Phase {
  name: string;
  startedAt: string;
  finishedAt?: string;
  status?: string;
  durationSeconds?: number;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

const phaseName = (e: BuildEvent): string | undefined => str(e.payload.phase) ?? str(e.payload.name);

const secondsBetween = (fromIso: string, toIso: string): number => (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000;

/** Closes the latest still-open phase of that name with the `phase_finished` event; a finish without a start is ignored. */
function finishPhase(phases: Phase[], e: BuildEvent, name: string): void {
  const open = [...phases].reverse().find((p) => p.name === name && !p.finishedAt);
  if (!open) return;
  open.finishedAt = e.createdAt;
  open.status = str(e.payload.status) ?? 'passed';
  const d = e.payload.durationSeconds;
  open.durationSeconds = typeof d === 'number' ? d : secondsBetween(open.startedAt, e.createdAt);
}

/** Pairs `phase_started` / `phase_finished` events by phase name (payload.phase or payload.name). Open phases run until `now`. */
export function phasesOf(events: BuildEvent[], now: number): Phase[] {
  const phases: Phase[] = [];
  for (const e of events) {
    const name = phaseName(e);
    if (!name) continue;
    if (e.kind === 'phase_started') phases.push({ name, startedAt: e.createdAt });
    else if (e.kind === 'phase_finished') finishPhase(phases, e, name);
  }
  for (const p of phases) if (!p.finishedAt) p.durationSeconds = (now - new Date(p.startedAt).getTime()) / 1000;
  return phases;
}

const KIND_ICON: Record<string, string> = {
  status_changed: 'commit',
  phase_started: 'play_arrow',
  phase_finished: 'check_circle',
  log_marker: 'terminal',
  note: 'description',
  attached: 'hub',
  cancel_requested: 'cancel',
  priority_changed: 'low_priority',
  check: 'shield',
  check_finished: 'shield',
};

export const kindIcon = (kind: string): string => KIND_ICON[kind] ?? 'bolt';

export interface Described {
  title: string;
  detail?: string;
}

const describeStatusChange = (e: BuildEvent): Described => ({
  title: `${e.fromStatus ? anyStatusLabel(e.fromStatus) : 'Created'} → ${e.toStatus ? anyStatusLabel(e.toStatus) : '?'}`,
  detail: str(e.payload.reason),
});

const describeNote = (e: BuildEvent): Described => ({ title: str(e.payload.message) ?? str(e.payload.title) ?? humanize(e.kind), detail: str(e.payload.detail) });

/** Checks and unknown kinds: show what the payload says (the contract tolerates new kinds). */
function describeOther(e: BuildEvent): Described {
  const p = e.payload;
  const key = str(p.key) ?? str(p.check);
  return { title: key ? `Check ${key} ${str(p.status) ?? ''}`.trim() : humanize(e.kind), detail: str(p.message) ?? str(p.summary) };
}

const DESCRIBERS = new Map<string, (e: BuildEvent) => Described>([
  ['status_changed', describeStatusChange],
  ['phase_started', (e) => ({ title: phaseEventTitle(phaseName(e), 'started'), detail: str(e.payload.message) })],
  ['phase_finished', (e) => ({ title: phaseEventTitle(phaseName(e), str(e.payload.status) ?? 'passed'), detail: str(e.payload.message) })],
  ['attached', () => ({ title: 'Build created on the build service' })],
  ['cancel_requested', (e) => ({ title: 'Cancel requested', detail: str(e.payload.reason) })],
  ['priority_changed', (e) => ({ title: `Priority ${str(e.payload.from) ?? ''} → ${str(e.payload.to) ?? ''}`, detail: str(e.payload.reason) })],
  ['note', describeNote],
  ['log_marker', describeNote],
]);

/** The title and optional detail line of an event (API text in it is reworded for clients by the timeline). */
export const describe = (e: BuildEvent): Described => (DESCRIBERS.get(e.kind) ?? describeOther)(e);

/** Event sources that name a provider ("eas", "bitrise"): clients read "build service" (SPEC decision 23). */
const PROVIDER_SOURCES = new Set(['eas', 'bitrise', 'provider']);

/** The source tag of an event, or null when it is Hangar itself; provider sources only name the provider for operators. */
export function eventSourceTag(source: string | null | undefined, seesProviders: boolean): string | null {
  if (!source || source === 'system') return null;
  return PROVIDER_SOURCES.has(source) && !seesProviders ? 'build service' : source;
}

const isFailedPhase = (p: Pick<Phase, 'status'>): boolean => p.status === 'failed' || p.status === 'error';

const LIVE_TONE = 'border-running/30 bg-running/10 text-running';
const FAILED_TONE = 'border-failed/30 bg-failed/10 text-failed';
const DONE_TONE = 'border-succeeded/30 bg-succeeded/10 text-succeeded';

/** Running phases are blue, failed ones red, the rest green. */
export function phaseTone(p: Phase): string {
  if (!p.finishedAt) return LIVE_TONE;
  return isFailedPhase(p) ? FAILED_TONE : DONE_TONE;
}

export function phaseIcon(p: Phase): string {
  if (!p.finishedAt) return 'radio_button_checked';
  return isFailedPhase(p) ? 'cancel' : 'check_circle';
}

const FAILED_STATUSES = new Set(['failed', 'errored', 'timed_out']);

/** Tone of an event's dot: a status change to a failure is red, to success green, anything else muted. */
export function eventDotTone(toStatus: string | null | undefined): string {
  if (toStatus && FAILED_STATUSES.has(toStatus)) return 'text-failed';
  return toStatus === 'succeeded' ? 'text-succeeded' : 'text-subtle';
}

/** Oldest first (ISO timestamps sort as text). */
export const sortedByTime = (events: BuildEvent[]): BuildEvent[] => [...events].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
